import {createSelector} from '@reduxjs/toolkit';

import {
	environmentOptions,
	iomoBaseOptions,
	iomoBracketOptions,
	iomoBuriedBaseSizeOptions,
	iomoDesignOptions,
	iomoFuseBoxOptions,
	iomoNumberOfHeadsOptions,
	iomoPoleHeightOptions,
	iomoPoleDiameterOptions,
	iomoPoleShapeOptions,
	iomoSecondDoorOptions,
	iomoSeparateBuriedBaseOptions
} from './iomoModel';
import {iomoColorThemeOptions, iomoMaterials} from './iomoMaterial';

import {selectLanguage, selectProductData, selectSpcAttributes, selectSpcConfiguration} from '../selectors';
import {getValueOfFeature, isDecided} from '../util/values';
import {getProductData} from '../util/getProductData';


export const selectIomoDesign = state => state.iomo.data.design;
export const selectIomoDesignIsSatisfied = state => state.iomo.data.design !== null;
export const selectIomoDesignData = createSelector(
	[selectIomoDesign],
	(design) => iomoDesignOptions.find(option => option.id === design)
);
export const selectIomoDesignIsPole = state => state.iomo.data.design === 'pole';
//export const selectIomoPoleHeight = state => state.iomo.data.general.poleHeight;
export const selectIomoPoleHeight = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		parseInt(getValueOfFeature('root::Poles::Pole::Basic::HS_Height', spcConfiguration), 10) / 1000
	)
);

export const selectIomoPoleHeightIsSatisfied = state => state.iomo.data.general.poleHeight !== null;
export const selectIomoPoleDiameter = state => state.iomo.data.general.poleDiameter;
export const selectIomoPoleDiameterIsSatisfied = state => state.iomo.data.general.poleDiameter !== null;
export const selectIomoPoleShape = state => state.iomo.data.general.poleShape;
export const selectIomoPoleShapeIsSatisfied = state => state.iomo.data.general.poleShape !== null;

export const selectIomoOffsetFromTop = createSelector([selectIomoDesign], (design) => (0.4));
export const selectIomoOffsetFromBottom = state => 3;

//export const selectIomoOpticalSystem = state => state.iomo.data.general.opticalSystem;
export const selectIomoOpticalSystem = createSelector([selectSpcConfiguration], (spcConfiguration) => {
		const headType = getValueOfFeature('root::Heads::Head::Housing::HS_HeadType', spcConfiguration);
		// depends on last letter (C, P)
		return headType !== null ? headType.endsWith('C') ? 'Comfort' : 'Performance' : null;
	}
);
//export const selectIomoOpticalSystemIsSatisfied = state => state.iomo.data.general.opticalSystem !== null;
export const selectIomoOpticalSystemIsSatisfied = createSelector([selectSpcAttributes], (spcAttributes) => (
	isDecided('root::Heads::Head::Housing::HS_HeadType', spcAttributes))
);
//export const selectIomoHousingSize = state => state.iomo.data.general.housingSize;
export const selectIomoHousingSize = createSelector([selectSpcConfiguration], (spcConfiguration) => {
		const headType = getValueOfFeature('root::Heads::Head::Housing::HS_HeadType', spcConfiguration);
		// last letter (C, P) cut off
		return headType !== null ? headType.slice(0, -1) : null;
	}
);
export const selectIomoHousingSizeIsSatisfied = createSelector([selectSpcAttributes], (spcAttributes) => (
	isDecided('root::Heads::Head::Housing::HS_HeadType', spcAttributes))
);


export const selectIomoColorTheme = state => state.iomo.data.general.colorTheme;
export const selectIomoColorThemeIsSatisfied = state => state.iomo.data.general.colorTheme !== null;
export const selectIomoColorThemeSwapped = state => state.iomo.data.general.colorThemeSwapped;

export const selectIomoCustomColor = state => state.iomo.data.general.customColor;

export const selectIomoEnvironment = state => state.iomo.data.general.environment;
export const selectIomoEnvironmentIsSatisfied = state => state.iomo.data.general.environment !== null;

export const selectIomoNumberOfHeads = state => state.iomo.data.general.numberOfHeads;
export const selectIomoNumberOfHeadsIsSatisfied = state => state.iomo.data.general.numberOfHeads !== null;
export const selectIomoMaxNumberOfHeads = state => state.iomo.data.general.maxNumberOfHeads;
export const selectIomoBracket = state => state.iomo.data.general.bracket;
export const selectIomoBracketIsSatisfied = state => state.iomo.data.general.bracket !== null;
export const selectIomoPoleMaterial = state => state.iomo.data.general.poleMaterial;
export const selectIomoPoleMaterialIsSatisfied = state => state.iomo.data.general.poleMaterial !== null;
export const selectIomoBase = state => state.iomo.data.general.base;
export const selectIomoBaseIsSatisfied = state => state.iomo.data.general.base !== null;
export const selectIomoSeparateBuriedBase = state => state.iomo.data.general.separateBuriedBase;
export const selectIomoSeparateBuriedBaseIsSatisfied = state => state.iomo.data.general.separateBuriedBase !== null;
export const selectIomoSecondDoor = state => state.iomo.data.general.secondDoor;
export const selectIomoSecondDoorIsSatisfied = state => state.iomo.data.general.secondDoor !== null;
export const selectIomoFuseBox = state => state.iomo.data.general.fuseBox;
export const selectIomoFuseBoxIsSatisfied = state => state.iomo.data.general.fuseBox !== null;
export const selectIomoBuriedBaseSize = state => state.iomo.data.general.buriedBaseSize;
export const selectIomoBuriedBaseSizeIsSatisfied = state => state.iomo.data.general.buriedBaseSize !== null;

export const selectIomoHousingColourInlay = state => state.iomo.data.general.housingColourInlay;
export const selectIomoHousingColourInlayIsSatisfied = state => state.iomo.data.general.housingColourInlay !== null;
export const selectIomoHousingColourPole = state => state.iomo.data.general.housingColourPole;
export const selectIomoHousingColourPoleIsSatisfied = state => state.iomo.data.general.housingColourPole !== null;

export const selectIomoLayout = state => state.iomo.data.general.layout;
export const selectIomoLayoutIsSatisfied = state => state.iomo.data.general.layout !== null;

export const selectIomoMaxHeight = state => state.iomo.data.general.heightMax;
export const selectIomoMinHeight = state => state.iomo.data.general.heightMin;
export const selectIomoMaxAngle = state => state.iomo.data.general.angleMax;
export const selectIomoMinAngle = state => state.iomo.data.general.angleMin;
export const selectIomoAllPitch = state => state.iomo.data.general.pitch;
export const selectIomoAllRoll = state => state.iomo.data.general.roll;

export const selectIomoSelectedHead = state => state.iomo.data.selectedHead;
export const selectIomoHeads = state => state.iomo.data.heads;

export const selectHeadTableVisible = state => state.iomo.data.headTableVisible;
export const selectTestMaterial1 = state => state.iomo.data.general.testMaterial1;
export const selectTestMaterial2 = state => state.iomo.data.general.testMaterial2;
export const selectUseTestMaterial = state => state.iomo.data.general.useTestMaterial;


export const selectIomoCustomColorUse = createSelector(
	[selectIomoCustomColor],
	(customColor) => (customColor.use)
);

export const selectIomoHeadsHaveCollisions = createSelector(
	[selectIomoDesign, selectIomoHeads],
	(design, heads) => (
		heads &&
		heads.some((head) => head.collisions?.length > 0))
);

export const selectIomoHeadSectionIsSatisfied = createSelector([selectSpcAttributes, selectIomoHeadsHaveCollisions], (spcAttributes, hasCollisions) => (
		!hasCollisions
		//isDecided('root::LE_ColourTemperature_1', spcAttributes) &&
		//isDecided('root::LE_LightDistribution_1', spcAttributes) &&
		//isDecided('root::LE_DimmingLevel_1', spcAttributes)
	)
);
export const selectIomoMiscSectionIsSatisfied = createSelector([selectSpcAttributes], (spcAttributes) => (
		true
		//isDecided('root::EB_Control_1', spcAttributes) &&
		//isDecided('root::EB_ConstantLumenTechnology_1', spcAttributes) &&
		//isDecided('root::EB_Zhaga_1', spcAttributes) &&
		//isDecided('root::EB_ElectricalProtectionClass_1', spcAttributes)
	)
);
export const selectIomoNumberOfHeadsValue = createSelector(
	[selectIomoNumberOfHeads],
	(numberOfHeads) => iomoNumberOfHeadsOptions.find(option => option.id === numberOfHeads + '').number
);

export const selectIomoPoleHeightValue = createSelector(
	[selectIomoPoleHeight],
	(poleHeight) =>
		iomoPoleHeightOptions.find(option => option.id === poleHeight + '')?.meter
);
export const selectIomoPoleDiameterValue = createSelector(
	[selectIomoPoleDiameter],
	(poleDiameter) =>
		iomoPoleDiameterOptions.find(option => option.id === poleDiameter + '')?.diameter
);

export const selectIomoColorThemeMaterialPole = createSelector(
	[selectIomoColorTheme, selectIomoColorThemeSwapped, selectIomoCustomColorUse, selectIomoCustomColor],
	(colorTheme, swapped, useCustomColor, customColor) => {
		if (useCustomColor) {
			if (customColor.base.used) {
				return iomoMaterials[customColor.base.material].material;
			}
		}
		return swapped ? iomoColorThemeOptions[colorTheme]?.materials?.ring.material : iomoColorThemeOptions[colorTheme]?.materials?.body.material;
	}
);

export const selectIomoColorThemeMaterialBody = createSelector(
	[selectIomoColorTheme, selectIomoColorThemeSwapped, selectIomoCustomColorUse, selectIomoCustomColor],
	(colorTheme, swapped, useCustomColor, customColor) => {
		if (useCustomColor) {
			if (customColor.head.used) {
				return iomoMaterials[customColor.head.material].material;
			}
			if (customColor.base.used) {
				return iomoMaterials[customColor.base.material].material;
			}
		}
		return swapped ? iomoColorThemeOptions[colorTheme]?.materials?.ring.material : iomoColorThemeOptions[colorTheme]?.materials?.body.material;
	}
);

export const selectIomoColorThemeMaterialRing = createSelector(
	[selectIomoColorTheme, selectIomoColorThemeSwapped, selectIomoCustomColorUse, selectIomoCustomColor],
	(colorTheme, swapped, useCustomColor, customColor) => {
		if (useCustomColor) {
			if (customColor.ring.used) {
				return iomoMaterials[customColor.ring.material].material;
			}
			if (customColor.head.used) {
				return iomoMaterials[customColor.head.material].material;
			}
			if (customColor.base.used) {
				return iomoMaterials[customColor.base.material].material;
			}
		}
		return swapped ? iomoColorThemeOptions[colorTheme]?.materials?.body.material : iomoColorThemeOptions[colorTheme]?.materials?.ring.material;
	}
);

export const selectIomoSelectedHeadHeight = createSelector(
	[selectIomoSelectedHead, selectIomoHeads],
	(selectedHead, heads) => (selectedHead !== null && selectedHead < heads.length ? heads[selectedHead].height : null)
);
export const selectIomoSelectedHeadAngle = createSelector(
	[selectIomoSelectedHead, selectIomoHeads],
	(selectedHead, heads) => (selectedHead !== null && selectedHead < heads.length ? heads[selectedHead].angle : null)
);

export const selectIomoEnvironmentTexture = createSelector(
	[selectIomoEnvironment],
	(environment) => environmentOptions.find(option => option.id === environment)?.texture
);
export const selectIomoDayTexture = createSelector(
	[selectIomoEnvironment],
	(environment) => environmentOptions.find(option => option.id === 'day')?.texture
);
export const selectIomoNightTexture = createSelector(
	[selectIomoEnvironment],
	(environment) => environmentOptions.find(option => option.id === 'night')?.texture
);
export const selectIomoEnvironmentBackground = createSelector(
	[selectIomoEnvironment],
	(environment) => environmentOptions.find(option => option.id === environment)?.background
);
export const selectIomoEnvironmentIntensity = createSelector(
	[selectIomoEnvironment],
	(environment) => environmentOptions.find(option => option.id === environment)?.intensity
);

// Generate selectors for heads
export function generateIomoHeadHeightSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].height);
}

export function generateIomoHeadAngleSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].angle);
}

export function generateIomoHeadPitchSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].pitch);
}

export function generateIomoHeadRollSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].roll);
}

export function generateIomoHeadHousingSizeSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].housingSize);
}

export function generateIomoHeadColourTemperatureSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].colourTemperature);
}

export function generateIomoHeadLightDistributionSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].lightDistribution);
}

export function generateIomoHeadReflectorColourSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].reflectorColour);
}

export function generateIomoHeadTypeSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].type);
}

export function generateIomoHeadModifierSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].modifier);
}

export function generateIomoHeadCapSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].cap);
}

export function generateIomoAccessoriesSelector(index) {
	return createSelector([selectIomoHeads], (heads) => heads[index].accessories);
}


// special selectors for iomo SPC values (will change when spc model is done)
/*
export const selectIomoColourTemperature = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::LE_ColourTemperature_1', spcConfiguration)
	)
);
export const selectIomoLightDistribution = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::LE_LightDistribution_1', spcConfiguration)
	)
);
*/

//export const selectIomoHousingSize = createSelector([selectSpcConfiguration], (spcConfiguration) => (
//		getValueOfFeature('root::HS_HousingSize', spcConfiguration)
//	)
//);


export const selectIomoReflectorColour = state => state.iomo.data.general.reflectorColour;
export const selectIomoColourTemperature = state => state.iomo.data.general.colourTemperature;
export const selectIomoLightDistribution = state => state.iomo.data.general.lightDistribution;
export const selectIomoModifier = state => state.iomo.data.general.modifier;
export const selectIomoCap = state => state.iomo.data.general.cap;
export const selectIomo3DReady = state => state.iomo.data.threeDReady;

export const selectIomoHousingColour = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::Heads::Head::ColourCoatingBase::HS_HousingColour', spcConfiguration)
	)
);
export const selectIomoDesignLabel = createSelector([selectIomoDesign, selectLanguage], (design, lang) => (
		iomoDesignOptions.find((designOption) => designOption.id === design).label[lang]
	)
);
export const selectIomoAccessories = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::Heads::Head::Type::Luminaire::LightEngine::LE_Accessories_1', spcConfiguration)
	)
);

export const selectIomoBaseDescription = createSelector([selectLanguage, selectIomoBase], (lang, base) => {
		const baseOptions = iomoBaseOptions.find((option) => option.id === base);
		return baseOptions.description[lang] ?? '';
	}
);
export const selectIomoSeparateBuriedBaseNumber = createSelector([selectLanguage, selectIomoSeparateBuriedBase, selectIomoPoleHeight], (lang, base, poleHeight) => {
		const options = iomoSeparateBuriedBaseOptions.find((option) => option.id === base);
		return options.poleHeights[poleHeight].number ?? '';
	}
);
export const selectIomoSeparateBuriedBaseHeight = createSelector([selectLanguage, selectIomoSeparateBuriedBase, selectIomoPoleHeight], (lang, base, poleHeight) => {
		const options = iomoSeparateBuriedBaseOptions.find((option) => option.id === base);
		return options.poleHeights[poleHeight].height ?? '';
	}
);
export const selectIomoSeparateBuriedBasePrice = createSelector([selectLanguage, selectIomoSeparateBuriedBase, selectIomoPoleHeight], (lang, base, poleHeight) => {
		const options = iomoSeparateBuriedBaseOptions.find((option) => option.id === base);
		return options.poleHeights[poleHeight].price ?? '';
	}
);
export const selectIomoSeparateBuriedBaseDescription = createSelector([selectLanguage, selectIomoSeparateBuriedBase], (lang, base) => {
		const baseOptions = iomoSeparateBuriedBaseOptions.find((option) => option.id === base);
		return baseOptions.description[lang] ?? '';
	}
);
export const selectIomoBaseNumber = createSelector([selectLanguage, selectIomoBase], (lang, base) => {
		const baseOptions = iomoBaseOptions.find((option) => option.id === base);
		return baseOptions.number ?? '';
	}
);
export const selectIomoPoleDescription = createSelector([selectLanguage, selectIomoDesign, selectIomoPoleMaterial], (lang, design, material) => {
		const designOptions = iomoDesignOptions.find((option) => option.id === design);
		return designOptions.pole[lang] ?? '';
	}
);
export const selectIomoBracketDescription = createSelector([selectLanguage, selectIomoBracket], (lang, bracket) => {
		const bracketOptions = iomoBracketOptions.find((option) => option.id === bracket);
		return bracketOptions.description[lang] ?? '';
	}
);

export const selectIomoBracketNumber = createSelector([selectIomoDesign, selectIomoBracket, selectIomoHousingSize],
	(design, bracket, housingSize) => {
		if (design === 'pole') {
			const bracketOption = iomoBracketOptions.find((option) => option.id === bracket);
			return bracketOption.number;
		}
		return '';
	});
export const selectIomoBracketPrice = createSelector([selectIomoDesign, selectIomoBracket, selectIomoHousingSize],
	(design, bracket, housingSize) => {
		if (design === 'pole') {
			const bracketOption = iomoBracketOptions.find((option) => option.id === bracket);
			return bracketOption.price;
		}
		return '';
	});

// design: pole
// material null, wood
// height: height in m
// diameter: diameter in mm
export const selectIomoPoleNumber = createSelector([selectIomoDesign, selectIomoPoleMaterial, selectIomoPoleHeightValue, selectIomoBase],
	(design, material, height, base) => {

		if (material === 'steel') {
			// Handle Pole design
			if (design === 'pole') {
				const poleOption = iomoPoleHeightOptions.find(option => option.meter === height);
				if (poleOption && poleOption.number) {
					return poleOption.number;
				}
				return '';
			}

		}

		// Return null for any other cases
		return '';
	}
);

// select base value, but return 'custom' when not all heads have the same value
export const selectIomoColourTemperatureInclCustom = createSelector([selectIomoColourTemperature, selectIomoHeads], (colourTemperature, heads) => (
		// if all heads properties match the general housingSize, return it, otherwise return "custom"
		colourTemperature === 'custom' || heads.every(head => head.colourTemperature === colourTemperature) ? colourTemperature : 'custom'
	)
);
export const selectIomoLightDistributionInclCustom = createSelector([selectIomoLightDistribution, selectIomoHeads], (lightDistribution, heads) => (
		// if all heads properties match the general value, return it, otherwise return "custom"
		lightDistribution === 'custom' || heads.every(head => head.lightDistribution === lightDistribution) ? lightDistribution : 'custom'
	)
);
export const selectIomoAccessoriesInclCustom = createSelector([selectIomoAccessories, selectIomoHeads], (accessories, heads) => (
		// if all heads properties match the general value, return it, otherwise return "custom"
		accessories === 'custom' || heads.every(head => head.accessories === accessories) ? accessories : 'custom'
	)
);
export const selectIomoHousingSizeInclCustom = createSelector([selectIomoHousingSize, selectIomoHeads], (housingSize, heads) => (
	(housingSize === 'custom' || heads.every(head => head.housingSize === housingSize)) ? housingSize : 'custom')
);
export const selectIomoReflectorColourInclCustom = createSelector([selectIomoReflectorColour, selectIomoHeads], (reflectorColour, heads) => (
	(reflectorColour === 'custom' || heads.every(head => head.reflectorColour === reflectorColour)) ? reflectorColour : 'custom')
);
export const selectIomoModifierInclCustom = createSelector([selectIomoModifier, selectIomoHeads], (modifier, heads) => (
		// if all heads properties match the general value, return it, otherwise return "custom"
		modifier === 'custom' || heads.every(head => head.modifier === modifier) ? modifier : 'custom'
	)
);

export const selectIomoCapInclCustom = createSelector([selectIomoCap, selectIomoHeads], (cap, heads) => (
		// if all heads properties match the general value, return it, otherwise return "custom"
		cap === 'custom' || heads.every(head => head.cap === cap) ? cap : 'custom'
	)
);

// selector for significant differences of head configuration compared to base
export function generateIomoHeadIsDifferentFromBase(index) {
	return createSelector([
		selectIomoHeads,
		selectIomoHousingSize,
		selectIomoHousingColour,
		selectIomoColourTemperature,
		selectIomoLightDistribution,
		selectIomoReflectorColour,
		selectIomoAccessories
	], (heads, housingSize, housingColour, colourTemperature, lightDistribution, reflectorColour, accessories) => {
		const head = heads[index];

		const headData = {
			type: head.type,
			housingSize: head.housingSize,
			colourTemperature: head.colourTemperature,
			lightDistribution: head.lightDistribution,
			reflectorColour: head.reflectorColour,
			cap: head.cap,
			modifier: head.modifier
		};
		const baseHeadData = {
			type: 'luminaire', // only luminaries in base
			housingSize: housingSize,
			colourTemperature: colourTemperature,
			lightDistribution: lightDistribution,
			reflectorColour: reflectorColour,
			cap: head.cap,
			modifier: head.modifier
		};
		return JSON.stringify(headData) !== JSON.stringify(baseHeadData);
	});
}

export const selectGroupedHeads = createSelector(
	[selectIomoHeads],
	(heads) => {
		let groupIndex = 0;
		const groupedHeads = heads.filter(head => head.active).reduce((groups, head, index) => {
			// Create a unique key using properties of the head
			const headData = {
				type: head.type,
				housingSize: head.housingSize,
				// housingColour: head.housingColour,
				colourTemperature: head.colourTemperature,
				lightDistribution: head.lightDistribution,
				reflectorColour: head.reflectorColour,
				cap: head.cap,
				modifier: head.modifier
			};

			const key = JSON.stringify(headData);

			if (!groups[key]) {
				groups[key] = {
					head: headData,
					groupIndex: groupIndex,
					instanceId: 'iomoHeadGroup_' + groupIndex,
					indexes: []
				};
				groupIndex++;
			}
			groups[key].indexes.push(index);

			return groups;
		}, {});

		// Convert the grouped object into an array of group values
		return Object.entries(groupedHeads).map(([key, value]) => value);
	});

export const selectIomoSecondDoorLabel = createSelector([selectLanguage, selectIomoSecondDoor], (lang, secondDoor) => {
		const options = iomoSecondDoorOptions.find((option) => option.id === secondDoor);
		return options?.label[lang] ?? '';
	}
);
export const selectIomoSecondDoorDescription = createSelector([selectLanguage, selectIomoSecondDoor, selectIomoDesign], (lang, secondDoor, design) => {
		const options = iomoSecondDoorOptions.find((option) => option.id === secondDoor);
		return options?.designs[design][lang] ?? '';
	}
);
export const selectIomoFuseBoxLabel = createSelector([selectLanguage, selectIomoFuseBox], (lang, fuseBox) => {
		const options = iomoFuseBoxOptions.find((option) => option.id === fuseBox);
		return options?.label[lang] ?? '';
	}
);
export const selectIomoFuseBoxNumber = createSelector([selectLanguage, selectIomoFuseBox], (lang, fuseBox) => {
		const options = iomoFuseBoxOptions.find((option) => option.id === fuseBox);
		return options?.number ?? '';
	}
);
export const selectIomoFuseBoxDescription = createSelector([selectLanguage, selectIomoFuseBox], (lang, fuseBox) => {
		const options = iomoFuseBoxOptions.find((option) => option.id === fuseBox);
		return options?.description[lang] ?? '';
	}
);
export const selectIomoBuriedBaseSizeSize = createSelector([selectLanguage, selectIomoBuriedBaseSize], (lang, base, size) => {
		const options = iomoBuriedBaseSizeOptions.find((option) => option.id === size);
		return options.size ?? '';
	}
);
